import { getImageKitUrl } from "core-lib/utils/imageKit";
import { CAR_IMAGE_FALLBACK } from "testDrive/constants/newCars";
import {
  BannerWidgetType,
  BrandListType,
  BrandsType,
  CompareWidgetType,
  FilterListKeyType,
  FilterListType,
  FilterPopupType,
  FilterType,
  FooterType,
  HeaderType,
  LandingPageType,
  NestedSliderListType,
  NestedSliderType,
  SearchType,
  SliderListType,
  SliderType,
  WIDGETS,
} from "../types/LandingPageTypes";

export const serializeHeader = (data): HeaderType => ({
  widgetType: data?.type,
  title: data?.title,
  subtitle: data?.sub_title,
  image: getImageKitUrl({
    product: "ImageKit",
    imageUrl: data?.image,
    width: 800,
  }),
});

export const serializeSearch = (data): SearchType => ({
  widgetType: data?.type,
  icon: getImageKitUrl({
    product: "ImageKit",
    imageUrl: data?.icon,
  }),
  placeholder: data?.placeholder,
});

export const serializeFilter = (data): FilterType => ({
  widgetType: data?.type,
  list: data?.props?.map(
    (item): FilterListType => ({
      type: item?.type,
      icon: getImageKitUrl({
        product: "ImageKit",
        imageUrl: item?.icon,
      }),
      text: item?.text,
      key: item?.key,
    })
  ),
});

export const serializeSlider = (data): SliderType => ({
  widgetType: data?.type,
  title: data?.title,
  subtitle: data?.sub_title,
  button: {
    variant: data?.cta?.variant || "ButtonGhostLink",
    title: data?.cta?.title,
    size: data?.cta?.size,
    redirectUrl: data?.cta?.redirection_url,
    filters: data?.cta?.filters,
  },
  list: data?.props?.map(
    (item): SliderListType => ({
      redirectionUrl: item?.redirection_url,
      subtitle: item?.sub_title,
      title: item?.title,
      image: getImageKitUrl({
        product: "ImageKit",
        imageUrl: item?.image || CAR_IMAGE_FALLBACK,
      }),
      expectedLaunchDate: item?.expected_launch,
      inventoryId: item?.inventory_id,
      modelId: item?.model_id,
      brandLogo: item?.brand_logo,
      brandName: item?.brand_name,
      modelName: item?.model_name,
    })
  ),
});

export const serializeNestedSlider = (data): NestedSliderType => {
  const arr = Object.keys(data?.props) || [];

  const tabList = [];

  const listObj = {};

  arr.map((item) => {
    (listObj[item] as NestedSliderListType) = {
      button: {
        title: data?.props?.[item]?.cta?.title,
        icon: getImageKitUrl({
          product: "ImageKit",
          imageUrl: data?.props?.[item]?.cta?.icon,
        }),
        size: data?.props?.[item]?.cta?.size,
        variant: data?.props?.[item]?.cta?.variant,
        redirectUrl: data?.props?.[item]?.cta?.redirection_url,
        filters: data?.props?.[item]?.cta?.filters,
      },
      list: data?.props?.[item]?.data?.map(
        (e): SliderListType => ({
          redirectionUrl: e?.redirection_url,
          title: e?.title,
          subtitle: e?.sub_title,
          image: getImageKitUrl({
            product: "ImageKit",
            imageUrl: e?.image || CAR_IMAGE_FALLBACK,
          }),
          inventoryId: e?.inventory_id,
          modelId: e?.model_id,
          brandLogo: e?.brand_logo,
          brandName: e?.brand_name,
          modelName: e?.model_name,
        })
      ),
    };
    tabList.push(data?.props?.[item]?.title);
  });

  const obj: NestedSliderType = {
    widgetType: data?.type,
    carouselList: Object.keys(data?.props),
    carouselType: data?.carousel_type,
    title: data?.title,
    subtitle: data?.sub_title,
    list: listObj,
    tabList,
  };

  return obj;
};

export const serializeBrands = (data): BrandsType => ({
  widgetType: data?.type,
  title: data?.title,
  subtitle: data?.sub_title,
  button: {
    variant: data?.cta?.variant || "ButtonGhostLink",
    title: data?.cta?.title,
    size: data?.cta?.size,
    redirectUrl: data?.cta?.redirection_url,
    icon: getImageKitUrl({
      product: "ImageKit",
      imageUrl: data?.cta?.icon,
    }),
    filters: data?.cta?.filters,
  },
  list: data?.props?.map(
    (item): BrandListType => ({
      image: getImageKitUrl({
        product: "ImageKit",
        imageUrl: item?.image || CAR_IMAGE_FALLBACK,
      }),
      title: item?.title,
      redirectionUrl: item?.redirection_url,
      filters: item?.filters,
    })
  ),
});

export const getValue = (value) => {
  if (value?.lte) {
    return { lte: value.lte };
  } else if (value?.gte) {
    return { gte: value.gte };
  }
};

export const serializeFilterPopup = (data): FilterPopupType => {
  const accordionList = Object.keys(data?.props) || [];

  const filterObj = {};

  accordionList?.map((item) => {
    filterObj[item] = {
      title: data?.props?.[item]?.title,
      list: data?.props?.[item]?.data?.map((e) => ({
        key: e?.key,
        title: e?.title,
        value:
          e?.type !== FilterListKeyType.PriceChip
            ? e?.value
            : getValue(e?.value),
        type: e?.type,
        minValue: {
          value: e?.min_value?.value,
          title: e?.min_value?.title,
        },
        maxValue: {
          value: e?.max_value?.value,
          title: e?.max_value?.title,
        },
      })),
    };
  });

  const obj: FilterPopupType = {
    widgetType: data?.type,
    accordionList: accordionList,
    list: filterObj,
  };
  return obj;
};

export const serializeFooter = (data): FooterType => ({
  widgetType: data?.type,
  list: {
    image: getImageKitUrl({
      product: "ImageKit",
      imageUrl: data?.props?.image,
    }),
  },
});

export const serializeCompareWidget = (data): CompareWidgetType => ({
  widgetType: data?.type,
  title: data?.props?.title,
  subtitle: data?.props?.sub_title,
});

export const serializeBannerWidget= (data) : BannerWidgetType => ({
  widgetType: data?.type,
  banners: data?.banners?.map((e) => ({
    redirectionUrl: e?.redirection_url,
    bannerUrl: e?.banner_url
  })),
})

export const serializeWidget = (type, data) => {
  switch (type) {
    case WIDGETS.Header:
      return serializeHeader(data);
    case WIDGETS.Search:
      return serializeSearch(data);
    case WIDGETS.Filter:
      return serializeFilter(data);
    case WIDGETS.Slider:
      return serializeSlider(data);
    case WIDGETS.NestedSlider:
      return serializeNestedSlider(data);
    case WIDGETS.Brands:
      return serializeBrands(data);
    case WIDGETS.FilterPopup:
      return serializeFilterPopup(data);
    case WIDGETS.Footer:
      return serializeFooter(data);
    case WIDGETS.CompareWidget:
      return serializeCompareWidget(data);
    case WIDGETS.BannerWidget:
        return serializeBannerWidget(data);
    default:
      return data;
  }
};

export const serializeLandingPageData = (data): LandingPageType => {
  const serializeData = [];

  data?.forEach((item) =>
    serializeData.push(serializeWidget(item?.type, item))
  );

  return serializeData;
};
